/**
 * Plinko math — standalone leaf module (no hono/drizzle) so the client can render
 * the bucket row and replay the ball without pulling the server bundle.
 *
 * 12 peg rows → 13 buckets. The ball's path is 12 coin flips taken from an HMAC
 * digest; the bucket is simply the number of rights (binomial). Payouts are
 * symmetric: dead-center pays 0.2x, the edges pay 175x. EV ≈ 0.990 (1% house
 * edge — asserted by test).
 *
 * Only landings ≥ 1.00x make the leaderboard (score = multiplier ×100, like
 * crash/mines/limbo); sub-1x buckets are duds, not scores.
 */

export const PLINKO_ROWS = 12;
export const PLINKO_BUCKETS = PLINKO_ROWS + 1;

/** Center→edge half-curve; full table is its mirror. Tuned for EV ≈ 0.990. */
const HALF = [0.2, 0.4, 0.9, 2, 5, 19, 175] as const;

/** Multiplier for bucket k (k = number of right-bounces, 0..12). */
export const PLINKO_MULTS: readonly number[] = Array.from(
  { length: PLINKO_BUCKETS },
  (_, k) => HALF[Math.abs(PLINKO_ROWS / 2 - k)]!,
);

/** Leaderboard bounds ×100: only ≥1x lands a score; edge bucket = 17500. */
export const PLINKO_MIN = 100;
export const PLINKO_MAX = 17_500;

export interface PlinkoDrop {
  /** 12 bounce bits, row by row: 0 = left, 1 = right. The client replays these. */
  path: number[];
  /** Landing bucket = number of rights (0..12). */
  bucket: number;
  mult: number;
}

/** Decode a drop from an HMAC digest (hex): first 12 bits = the bounce path. */
export function plinkoDrop(digestHex: string): PlinkoDrop {
  const bits12 = Number.parseInt(digestHex.slice(0, 3), 16);
  const path: number[] = [];
  let bucket = 0;
  for (let row = 0; row < PLINKO_ROWS; row++) {
    const bit = (bits12 >> row) & 1;
    path.push(bit);
    bucket += bit;
  }
  return { path, bucket, mult: PLINKO_MULTS[bucket]! };
}

export function plinkoToScoreValue(mult: number): number {
  return Math.min(Math.max(Math.round(mult * 100), PLINKO_MIN), PLINKO_MAX);
}
